import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import { useSecurity } from "../../../auth/registered/SEC_03_03_SecurityContext";
import { useCompanyContext } from "../../common/company/CompanyContext";
import { fetchCorporateRegistry } from "../../corporate_details/corporateInteractionService";
import { REPORTING_STEPS, REPORTING_EXTRAS } from "./reportingFlow";
import ReportManagementShell from "./ReportManagementShell";

/**
 * MANAGE ESG REPORTING — hub
 *
 * One entry point for authoring an ESG report, split out of Site Administration.
 * Reached from a "Manage ESG Reporting" card on the Registered dashboard.
 *
 * Tiles are numbered and ordered on purpose: work straight down 1 → 6. Card text
 * says what each step produces; the screen you land on repeats it with its
 * dependencies (ReportingStepFrame). Source of truth for both: reportingFlow.js.
 *
 * The company selector sets the platform-wide active company (CompanyContext),
 * so every step below is scoped to it.
 */
export default function ReportManagementHub() {
  const navigate = useNavigate();
  const siteKey = process.env.REACT_APP_SITE_KEY;
  const { myCorporateEntityIds, isGlobalAdmin } = useSecurity();
  const { activeCompanyId, activeCompanyName, setActiveCompany } = useCompanyContext();

  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingCompanies(true);
      try {
        const registry = await fetchCorporateRegistry({ siteKey });
        const rows = (registry || [])
          .map((c) => ({
            id: String(GoldenSourceHelper.get(c, "CorporateEntityId")),
            name:
              GoldenSourceHelper.get(c, "EntityName") ||
              GoldenSourceHelper.get(c, "Name") ||
              `Company ${GoldenSourceHelper.get(c, "CorporateEntityId")}`,
          }))
          .filter((c) => c.id && c.id !== "null" && c.id !== "undefined");
        // A Global Admin sees every company; everyone else only the ones they
        // hold a security-matrix row for.
        const mine = isGlobalAdmin
          ? rows
          : rows.filter((c) => myCorporateEntityIds.includes(c.id));
        if (alive) setCompanies(mine.sort((a, b) => a.name.localeCompare(b.name)));
      } catch {
        if (alive) setCompanies([]);
      } finally {
        if (alive) setLoadingCompanies(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [siteKey, isGlobalAdmin, myCorporateEntityIds]);

  const onPickCompany = (e) => {
    const id = e.target.value;
    if (!id) return;
    const picked = companies.find((c) => c.id === id);
    setActiveCompany(id, picked ? picked.name : null);
  };

  const hasCompanies = companies.length > 0;
  const selectValue = useMemo(
    () => (activeCompanyId != null ? String(activeCompanyId) : ""),
    [activeCompanyId],
  );

  return (
    <ReportManagementShell>
      <main id="main" className="page-main" data-scroll-container>
        <div className="page-main-inner">
          <section className="page-section">
            <div className="container mt-4">
              <div className="row mb-3">
                <div className="col">
                  <h2 className="h4 fw-bold">Manage ESG Reporting</h2>
                  <p className="text-muted small mb-0" style={{ maxWidth: 760 }}>
                    Everything needed to author an ESG report. The steps are in order
                    &mdash; work down from 1. Each card says what that step produces;
                    open it for the detail and what it needs from earlier steps.
                  </p>
                </div>
              </div>

              {/* COMPANY SELECTOR */}
              <div className="card border-0 shadow-sm bg-light mb-4">
                <div className="card-body py-3 d-flex flex-wrap align-items-center gap-3">
                  <label
                    className="extra-small fw-bold text-uppercase text-muted mb-0"
                    htmlFor="rm-company"
                    style={{ letterSpacing: "0.5px" }}
                  >
                    Working on company
                  </label>
                  {loadingCompanies ? (
                    <span className="small text-muted">
                      <span className="spinner-border spinner-border-sm me-2" />
                      Loading your companies…
                    </span>
                  ) : hasCompanies ? (
                    <>
                      <select
                        id="rm-company"
                        className="form-select form-select-sm"
                        style={{ width: "auto", minWidth: 260 }}
                        value={selectValue}
                        onChange={onPickCompany}
                      >
                        <option value="">Select a company…</option>
                        {companies.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                      {activeCompanyName && (
                        <span className="badge bg-dark">{activeCompanyName}</span>
                      )}
                    </>
                  ) : (
                    <span className="small text-muted">
                      You are not assigned to any company yet &mdash; start with{" "}
                      <button
                        type="button"
                        className="btn btn-link p-0 align-baseline small"
                        onClick={() => navigate(REPORTING_STEPS[0].path)}
                      >
                        step 1, Register a Company
                      </button>
                      .
                    </span>
                  )}
                </div>
              </div>

              {/* NUMBERED FLOW */}
              <div className="row g-4">
                {REPORTING_STEPS.map((tool) => (
                  <div key={tool.key} className="col-12 col-sm-6 col-md-4">
                    <div
                      className="card h-100 shadow-sm border dashboard-card"
                      onClick={() => navigate(tool.path)}
                      style={{ cursor: "pointer", transition: "transform 0.2s" }}
                    >
                      <div className="card-body d-flex flex-column align-items-center text-center py-4">
                        <span
                          className="badge bg-dark rounded-circle mb-2 d-flex align-items-center justify-content-center"
                          style={{ width: 28, height: 28, fontSize: "0.8rem" }}
                        >
                          {tool.n}
                        </span>
                        <img
                          src={tool.icon}
                          alt=""
                          className="mb-3"
                          style={{ width: "56px", height: "56px", objectFit: "contain" }}
                        />
                        <h6 className="card-title mb-1 fw-bold">{tool.name}</h6>
                        <p className="text-muted small mb-0 px-2">{tool.blurb}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {REPORTING_EXTRAS.length > 0 && (
                <>
                  <div
                    className="text-uppercase text-muted mt-5 mb-2"
                    style={{ fontSize: "0.65rem", letterSpacing: "1px" }}
                  >
                    Also available &mdash; not part of the numbered flow
                  </div>
                  <div className="row g-4">
                    {REPORTING_EXTRAS.map((tool) => (
                      <div key={tool.key} className="col-12 col-sm-6 col-md-4">
                        <div
                          className="card h-100 shadow-sm border dashboard-card opacity-75"
                          onClick={() => navigate(tool.path)}
                          style={{ cursor: "pointer", transition: "transform 0.2s" }}
                        >
                          <div className="card-body d-flex flex-column align-items-center text-center py-4">
                            <img
                              src={tool.icon}
                              alt=""
                              className="mb-3"
                              style={{ width: "56px", height: "56px", objectFit: "contain" }}
                            />
                            <h6 className="card-title mb-1 fw-bold">{tool.name}</h6>
                            <p className="text-muted small mb-0 px-2">{tool.blurb}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <div className="mt-5 border-top pt-3">
                <button
                  className="btn btn-link text-decoration-none p-0 text-secondary small"
                  onClick={() => navigate("/registered/dashboard")}
                >
                  &larr; Return to Registered Dashboard
                </button>
              </div>
            </div>
          </section>
        </div>
      </main>
    </ReportManagementShell>
  );
}
